<template>
  <Modal class="loan-details-modal" :closeBtn="true" @close="closeModal">
    <div class="loans-to-refinance-section-header-wrapper">
      <p class="title">Loan Details</p>
      <p>
        If the payoff information below is incorrect or if you wish to pay off an additional loan
        from your pre-approved loan amount, please contact
        <a class="email-link" :href="`mailto:${emailAddress}`">{{ emailAddress }}.</a>
      </p>
    </div>
    <div class="loans-to-refinance-section-wrapper">
      <CircleLoader :inline="true" v-if="gettingData"></CircleLoader>
      <LoansToRefinanceTable
        v-else
        :showPayoffDetails="showPayoffDetails"
        :showActionsColumn="true"
        :showTableHeader="false"
        :showAddLoanButton="true"
        :showIncludedLoansOnly="finalDocumentationStipExists"
        id="Parent_LoanDetailsModal"
      ></LoansToRefinanceTable>
    </div>
    <dialogue-modal
      v-if="showDialogueModal === true"
      @cancel="showDialogueModal = false"
      @confirm="handleDialogueModal"
      :title="`Quit editing?`"
      :msg="`Any changes you\'ve made so far will not be saved`"
      :confirmButtonText="'Yes'"
      :cancelButtonText="'No'"
    />
    <div v-if="updateRefiLoansError">
      An error occurred while processing your request. Please try again later. If the issue
      persists, please contact our support team.
    </div>
    <template v-slot:footer>
      <div class="error" v-show="loanMinNotMetError">
        {{ minimumLoanAmountDisplay }} is the minimum refinance amount.
      </div>
      <div class="step-btns">
        <next-button
          text="Cancel"
          @click.prevent="closeModal"
          :disabled="savingChanges || discardingChanges"
          :loading="discardingChanges"
        >
        </next-button>
        <next-button
          text="Save Changes"
          @click.prevent="confirmChanges"
          :disabled="savingChanges"
          :loading="savingChanges"
        >
        </next-button>
      </div>
    </template>
  </Modal>
</template>
<script setup lang="ts">
import { ref, defineEmits, computed, onMounted, watch, onUnmounted } from "vue";
import { useStore } from "vuex";
import { UpdateRefiLoansRequest } from "@/models/vuex";
import Modal from "@/components/shared/Modal.vue";
import DialogueModal from "@/components/modals/DialogueModal.vue";
import LoansToRefinanceTable from "@/components/refi-loans-table/LoansToRefinanceTable.vue";
import NextButton from "@/components/shared/NextButton.vue";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import _ from "lodash";

const store = useStore();
const showDialogueModal = ref<boolean>(false);

const emit = defineEmits(["close"]);

const gettingData = ref<boolean>(true);
const savingChanges = ref<boolean>(false);
const discardingChanges = ref<boolean>(false);

const showPayoffDetails = computed(() =>
  store.state.applicationInfoModule.currentlyActiveModals?.includes("ConfirmYourRefinanceDetails"),
);
const updatingRefiLoans = computed(() => store.state.applicationInfoModule?.updatingRefiLoans);
const loans = computed(() => store?.getters["applicationInfoModule/RefiLoans"]);
const emailAddress = computed(() => store.getters["contactUs"]?.email);
const currentApplicant = computed(() => store.state.appData.currentApplicant);
const finalDocumentationStipExists = computed(
  () => store.getters["checklistModule/finalDocumentationStipExists"],
);
const updateRefiLoansError = computed(
  () => store.getters["applicationInfoModule/updateRefiLoansError"],
);

const minimumLoanAmount = computed(() => store.getters["PROGRAM_MINIMUM_LOAN_AMOUNT"]);

const minimumLoanAmountDisplay = computed(
  () => store.getters["PROGRAM_MINIMUM_LOAN_AMOUNT_DISPLAY"],
);

const calculatedEstimatedTotalToRefinance = computed<boolean>(
  () => store.state.applicationInfoModule.calculatedEstimatedTotalToRefinance,
);

const loanMinNotMetError = computed<boolean>(
  () => calculatedEstimatedTotalToRefinance.value < minimumLoanAmount.value,
);

function openDialogueModal() {
  showDialogueModal.value = true;
}

function handleDialogueModal() {
  store.dispatch("applicationInfoModule/discardChanges");
  store.dispatch("applicationInfoModule/closeModal", "LoanDetails");
  showDialogueModal.value = false;
}

function closeModal() {
  openDialogueModal();
}

async function confirmChanges() {
  savingChanges.value = true;

  if (!loanMinNotMetError.value) {
    const updateRefiLoansRequest = {
      applicationId: store.state.appData?.application?.id,
      applicantId: currentApplicant.value?.id,
      request: loans.value,
    } as UpdateRefiLoansRequest;

    await store.dispatch("applicationInfoModule/updateRefiLoans", updateRefiLoansRequest);
  } else {
    savingChanges.value = false;
  }
}

async function getRefiLoanDetailsModal() {
  await store.dispatch(
    "applicationInfoModule/getRefiLoanModalData",
    store.state.appData?.application?.id,
  );

  gettingData.value = false;
}

onMounted(() => {
  gettingData.value = true;
  getRefiLoanDetailsModal();
  const originalRefiLoans = _.cloneDeep(loans.value);
  store.commit("applicationInfoModule/SET_ORIGINAL_REFI_LOANS", originalRefiLoans);
});

onUnmounted(() => {
  store.dispatch(
    "applicationInfoModule/setCalculatedEstimatedTotalToRefinance",
    store?.state?.applicationInfoModule?.applicationInfo?.estimatedTotal,
  );
});

watch(
  () => updateRefiLoansError.value,
  async (value: any) => {
    if (value) {
      savingChanges.value = false;
    }
  },
);
</script>

<style scoped lang="scss">
.loans-to-refinance-section-header-wrapper {
  padding: 0 1em;
}
.loans-to-refinance-section-header-wrapper p {
  margin: 0;
}
.loans-to-refinance-section-header-wrapper .title {
  font-weight: 700;
  font-size: 1.25rem;
}
.loans-to-refinance-section-wrapper {
  padding: 1em;
}
.email-link {
  font-family: var(--body-font);
  font-weight: 400;
  text-decoration: underline;
  cursor: pointer;
}

.error {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.step-btns {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-end;
  width: 100%;
  gap: 12px;
}

@media (max-width: 576px) {
  .step-btns button {
    flex: 1 1 33%;
  }
}

@media (max-width: 300px) {
  .step-btns {
    flex-direction: column;
  }
  .step-btns button {
    flex: 1;
  }
}
</style>
